import { useEffect, useRef, useState } from 'react';
import { ArrowRight, Loader2 } from 'lucide-react';
import { FormErrorBanner } from '../FormErrorBanner';
import type { HiringLink, HiringStoreOption } from '../../services/api';
import {
  hiringMoveAPI,
  type HiringMoveInput,
  type HiringMovePreview,
  type HiringMoveResult,
} from '../../services/hiringRecruitmentApi';
import { CitySelect, StorePicker, type PickedCity } from './HiringPickers';
import {
  MOVE_CODES,
  MOVE_WORDS,
  NO_MOVE_NEEDS,
  buildMoveBody,
  hiringCodeOf,
  hiringErrorText,
  hiringStatusOf,
  isHiringOff,
  localiseRaiseMessage,
  makeMoveRequestKey,
  moveAddressLine,
  moveCheckIsNews,
  moveErrorText,
  moveNeedsAfter,
  moveRefusalStep,
  moveStoreLabel,
  openingStoreWords,
  type MoveNeeds,
} from './hiringText';

/**
 * "Move to another store", in the foot of one opening's window (15 Oct 2026, the product
 * owner: "Yes, build the Move to another store button"). It asks Recruitment to move a live
 * opening ChannelPlex follows for ONE store to another store of the project; Recruitment
 * changes only its city, address, map point and PIN code, mails its recruiters, offer makers
 * and interviewers, and tells candidates in process who use the ChannelPlay Jobs app.
 *
 *   1. Pick     the new store, in the house's store picker (a page of 50 at a time, to the
 *               end; the store it is followed for now greyed). A Recruitment city and the
 *               ticks appear only when the review's refusal asks for them.
 *   2. Review   the server's own title, sentences and buttons, with from -> to, the new
 *               address, the distance and the "already there" line. NOTHING IS SENT.
 *   3. Confirm  "Yes, move this opening": ONE call, with the key made when the review
 *               arrived. Never repeated by itself; "Ask again" (after no answer) repeats the
 *               very same request - the same key, body and hash - and the server answers a
 *               repeat from the first, so one review can never move twice.
 *
 * How it ended is the card's to show (`onResult`): moved, the server's sentence, or - while
 * Recruitment has not confirmed - the move's box with "Check again" (MoveStatus), and no
 * second move. Mounted by the card only where Cancel would be offered on a store's own
 * opening; the card decides that.
 */

type Step = 'pick' | 'reading' | 'review' | 'sending' | 'no_answer' | 'blocked';

const SMALL_BUTTON = 'rounded-full border px-3 py-1 text-xs font-semibold transition-colors disabled:opacity-50';
const PRIMARY_SMALL = `${SMALL_BUTTON} inline-flex items-center gap-1 border-primary-600 bg-primary-600 text-white hover:bg-primary-700`;
const QUIET_SMALL = `${SMALL_BUTTON} border-gray-300 bg-white text-gray-700 hover:bg-gray-50`;
// A step brings itself into view with the foot's own padding under it, not flush on the window's bottom edge.
const IN_VIEW = 'scroll-mb-4';

export interface MoveFromStore { storeId: string; storeName: string | null; storeCode: string | null }

export function MoveStoreStep({
  projectId, link, from, cancelOpen = false, onResult, onDone, onMoveGone, onHiringGone, onListStale, onLinkGone,
}: {
  projectId: number;
  link: HiringLink;
  /** The store the opening is followed for now. */
  from: MoveFromStore;
  /** The card shows a cancel of this opening open (its own box, with its own "Check again"): the move's is not offered beside it. */
  cancelOpen?: boolean;
  /** A move, or a check of one, was answered once a claim existed. */
  onResult: (result: HiringMoveResult) => void;
  /** The window is closed (by "Close", "No", or after an answer): the foot's buttons are back. */
  onDone: () => void;
  /**
   * Move (the project's Cancel switch) was switched off, or stopped on the server, since the page asked.
   * `lost`: it was "Ask again" of a confirm whose answer never came back - the move may have gone out.
   */
  onMoveGone: (lost: boolean) => void;
  onHiringGone: () => void;
  /**
   * The page's list must be read again, and the card kept on show whatever it brings.
   * 'lost': no answer came back from the confirm, so the move may have gone out.
   */
  onListStale: (why: 'refused' | 'lost') => void;
  /** Someone stopped following it, or cancelled it, first. The card says so in `sentence`. */
  onLinkGone: (sentence: string) => void;
}) {
  const [step, setStep] = useState<Step>('pick');
  const [store, setStore] = useState<HiringStoreOption | null>(null);
  const [needs, setNeeds] = useState<MoveNeeds>(NO_MOVE_NEEDS);
  const [city, setCity] = useState<PickedCity | null>(null);
  const [confirmStatus, setConfirmStatus] = useState(false);
  const [confirmAdditional, setConfirmAdditional] = useState(false);
  const [preview, setPreview] = useState<HiringMovePreview | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  // Said above a review that replaced one the server found out of date, or beside "Ask again".
  const [notice, setNotice] = useState<string | null>(null);
  // Something sent for this opening is still open: the refusal offers "Check again" (reads, never sends).
  const [offerCheck, setOfferCheck] = useState(false);
  const [checking, setChecking] = useState(false);

  // The review on show: what it was asked with, and the key of its confirm - made when it
  // arrived, and never for another review.
  const reviewed = useRef<HiringMoveInput | null>(null);
  const requestKey = useRef<string | null>(null);
  // No answer came back from this confirm: from then on only the same request may be repeated.
  const lost = useRef(false);
  // One call at a time, whatever asks for it.
  const working = useRef(false);
  const alive = useRef(true);
  const owner = useRef({ onResult, onDone, onMoveGone, onHiringGone, onListStale, onLinkGone });
  owner.current = { onResult, onDone, onMoveGone, onHiringGone, onListStale, onLinkGone };

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  // Each step brings itself into view in the window's scrolling body: the review from its
  // title down, every other step as little as needed. (jsdom has no scrollIntoView.)
  const box = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = box.current;
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: step === 'review' ? 'start' : 'nearest' });
  }, [step]);

  const fromWords = openingStoreWords(from);

  /** Move, or hiring itself, is off; or the link is no longer followed: the owner acts. True when that was it. */
  const handledByOwner = (err: unknown): boolean => {
    if (isHiringOff(err)) {
      owner.current.onHiringGone();
      return true;
    }
    const kind = moveRefusalStep(err);
    if (kind === 'control_goes') {
      owner.current.onMoveGone(lost.current);
      return true;
    }
    if (kind === 'link_gone') {
      owner.current.onLinkGone(hiringErrorText(err, MOVE_WORDS.reviewFailed));
      return true;
    }
    return false;
  };

  /** The refusal of a review or a confirm, said on step 1 with what it asks for. */
  const backToPick = (err: unknown) => {
    setNeeds((was) => moveNeedsAfter(was, err));
    setRefusal(moveErrorText(err, MOVE_WORDS.reviewFailed));
    setPreview(null);
    reviewed.current = null;
    requestKey.current = null;
    setStep('pick');
  };

  /** Something sent for this opening is still open (a move or a cancel): its sentence, and "Check again". */
  const blockedByOpenSend = (err: unknown) => {
    owner.current.onListStale('refused');
    if (!alive.current) return;
    setRefusal(moveErrorText(err, MOVE_WORDS.reviewFailed));
    setOfferCheck(true);
    setStep('blocked');
  };

  /** The review: every check, and what Recruitment will do. Sends nothing, saves nothing. */
  const review = async (body: HiringMoveInput, said: string | null = null) => {
    if (working.current) return;
    working.current = true;
    setStep('reading');
    setRefusal(null);
    setNotice(said);
    try {
      const answered = await hiringMoveAPI.preview(projectId, link.id, body);
      if (!alive.current) return;
      if (!answered || typeof answered.previewHash !== 'string' || !Array.isArray(answered.sentences)) {
        setRefusal(MOVE_WORDS.reviewFailed);
        setStep('pick');
        return;
      }
      // One key per review, made as it arrives: a second review is a second confirm.
      reviewed.current = body;
      requestKey.current = makeMoveRequestKey();
      lost.current = false;
      setPreview(answered);
      setStep('review');
    } catch (err) {
      if (handledByOwner(err)) return;
      const next = moveRefusalStep(err);
      if (next === 'check') {
        blockedByOpenSend(err);
        return;
      }
      if (next === 'list_reread') owner.current.onListStale('refused');
      if (!alive.current) return;
      backToPick(err);
    } finally {
      working.current = false;
    }
  };

  const askReview = () => {
    if (!store) return;
    void review(buildMoveBody({ storeId: store.storeId, needs, cityId: city?.cityId ?? null, confirmStatus, confirmAdditional }));
  };

  /** "Yes, move this opening", and every "Ask again" after it: the same key, body and hash. */
  const confirm = async () => {
    const key = requestKey.current;
    const body = reviewed.current;
    if (!preview || !key || !body || working.current) return;
    working.current = true;
    setStep('sending');
    setRefusal(null);
    try {
      const answered = await hiringMoveAPI.move(projectId, link.id, { ...body, requestKey: key, previewHash: preview.previewHash });
      if (!answered || typeof answered.outcome !== 'string' || !answered.link) {
        // An answer that cannot be read is no answer: only the same request may be repeated.
        lost.current = true;
        owner.current.onListStale('lost');
        if (alive.current) {
          setNotice(null);
          setStep('no_answer');
        }
        return;
      }
      // The owner is told whether or not this window is still on show.
      owner.current.onResult(answered);
      requestKey.current = null;
      reviewed.current = null;
      if (alive.current) owner.current.onDone();
    } catch (err) {
      if (handledByOwner(err)) return;
      const status = hiringStatusOf(err);
      const refused = status !== undefined && status < 500 && !!hiringCodeOf(err);
      if (!refused) {
        // No answer, or an error of the server's own: the move may have gone out.
        lost.current = true;
        owner.current.onListStale('lost');
        if (!alive.current) return;
        setNotice(hiringCodeOf(err) ? moveErrorText(err, '') || null : null);
        setStep('no_answer');
        return;
      }
      // A definite "no" from the checks: nothing was sent by this request.
      const next = moveRefusalStep(err);
      if (next === 'check') {
        blockedByOpenSend(err);
        return;
      }
      if (next === 'list_reread' || lost.current) owner.current.onListStale('refused');
      if (!alive.current) return;
      if (lost.current) {
        // An earlier answer of this very confirm was lost: no fresh review is offered. The sentence, and "Close".
        setRefusal(moveErrorText(err, MOVE_WORDS.reviewFailed));
        setOfferCheck(false);
        setStep('blocked');
        return;
      }
      if (next === 'review_again') {
        // What was reviewed is no longer so. A fresh review of the same pick, with a new key.
        working.current = false;
        await review(body, MOVE_WORDS.reviewedAgain);
        return;
      }
      backToPick(err);
    } finally {
      working.current = false;
    }
  };

  /** "Check again" on a refusal that says something sent is still open: reads, never sends. */
  const check = async () => {
    if (working.current) return;
    working.current = true;
    setChecking(true);
    try {
      const result = await hiringMoveAPI.check(projectId, link.id);
      if (result && typeof result.outcome === 'string') {
        // The check answers with the link's LATEST move, whatever became of it. Only a move still
        // waiting (or one that took the opening elsewhere) is what blocks this one; the ending
        // of an earlier move is not said as if it had just happened. Then what is open is a
        // cancel: the owner's list, read again, draws its box.
        if (!moveCheckIsNews(result, from.storeId)) {
          owner.current.onListStale('refused');
          if (!alive.current) return;
          setOfferCheck(false);
          setRefusal(MOVE_WORDS.noMoveWaiting);
          return;
        }
        owner.current.onResult(result);
        if (alive.current) owner.current.onDone();
        return;
      }
      if (alive.current) setRefusal(MOVE_WORDS.checkFailed);
    } catch (err) {
      if (isHiringOff(err)) {
        owner.current.onHiringGone();
        return;
      }
      if (!alive.current) return;
      // No move on record: what is open is a cancel, and its own box (after the re-read) checks it.
      if (hiringCodeOf(err) === MOVE_CODES.linkNotMoving) setOfferCheck(false);
      setRefusal(moveErrorText(err, MOVE_WORDS.checkFailed));
    } finally {
      working.current = false;
      if (alive.current) setChecking(false);
    }
  };

  const pickStore = (next: HiringStoreOption | null) => {
    setStore(next);
    // What a refusal asked for belonged to the store it was about.
    setNeeds(NO_MOVE_NEEDS);
    setCity(null);
    setConfirmStatus(false);
    setConfirmAdditional(false);
    setRefusal(null);
  };

  const groupLabel = MOVE_WORDS.groupLabel(link.openingId);

  if (step === 'sending' || step === 'no_answer') {
    const sending = step === 'sending';
    return (
      <div ref={box} role="group" aria-label={MOVE_WORDS.confirmGroupLabel(link.openingId)} className={`${IN_VIEW} space-y-2 border-t border-gray-100 pt-3`}>
        {sending ? (
          <p role="status" className="inline-flex items-center gap-2 text-sm text-gray-800">
            <Loader2 className="h-4 w-4 animate-spin" />
            {MOVE_WORDS.sending}
          </p>
        ) : (
          <>
            <p role="alert" className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">{notice || MOVE_WORDS.noAnswer}</p>
            <button type="button" autoFocus onClick={() => void confirm()} className={QUIET_SMALL}>
              {MOVE_WORDS.askAgain}
            </button>
          </>
        )}
      </div>
    );
  }

  if (step === 'blocked') {
    return (
      <div ref={box} role="group" aria-label={groupLabel} className={`${IN_VIEW} space-y-2 border-t border-gray-100 pt-3`}>
        <p role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{refusal}</p>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" autoFocus onClick={() => owner.current.onDone()} className={QUIET_SMALL}>
            {MOVE_WORDS.close}
          </button>
          {/* Not beside a cancel's own box: that box checks what is open. */}
          {offerCheck && !cancelOpen && (
            <button type="button" onClick={() => void check()} disabled={checking} className={`${QUIET_SMALL} inline-flex items-center gap-1`}>
              {checking && <Loader2 className="h-3 w-3 animate-spin" />}
              {checking ? MOVE_WORDS.checking : MOVE_WORDS.checkAgain}
            </button>
          )}
        </div>
      </div>
    );
  }

  if (step === 'review' && preview) {
    const to = preview.to;
    const toStore = moveStoreLabel(to.store);
    const fromStore = moveStoreLabel(preview.from.store);
    const fromAddress = moveAddressLine(preview.from.address);
    // The location alone: the line under it gives the city, the state and the PIN code.
    const toAddress = moveAddressLine({ location: to.address.location });
    const cityWords = [to.address.cityName, to.address.stateName].map((part) => String(part ?? '').trim()).filter(Boolean).join(', ');
    return (
      <div
        ref={box}
        role="group"
        aria-label={MOVE_WORDS.confirmGroupLabel(preview.link?.openingId ?? link.openingId)}
        className={`${IN_VIEW} space-y-3 rounded-lg border border-primary-200 bg-primary-50/40 p-3`}
      >
        {notice && <p role="status" className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">{notice}</p>}
        <p className="text-sm font-semibold text-gray-900">{preview.title}</p>
        {/* From -> to: side by side on a laptop, one under the other on a phone. */}
        <div className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] sm:items-start">
          <div className="min-w-0 rounded-lg bg-white px-3 py-2">
            <p className="text-xs font-semibold text-gray-500">{MOVE_WORDS.from}</p>
            <p className="break-words font-medium text-gray-900">{fromStore}</p>
            {fromAddress && <p className="break-words text-xs text-gray-600">{fromAddress}</p>}
          </div>
          <ArrowRight className="hidden h-4 w-4 self-center text-gray-400 sm:block" aria-hidden="true" />
          <div className="min-w-0 rounded-lg bg-white px-3 py-2">
            <p className="text-xs font-semibold text-gray-500">{MOVE_WORDS.to}</p>
            <p className="break-words font-medium text-gray-900">{toStore}</p>
            {toAddress && <p className="break-words text-xs text-gray-600">{toAddress}</p>}
            <p className="text-xs text-gray-600">{[cityWords, to.address.pincode ? MOVE_WORDS.pin(to.address.pincode) : null].filter(Boolean).join(' · ')}</p>
            {typeof to.distanceKm === 'number' && (
              <p className="text-xs text-gray-500">{MOVE_WORDS.distance(to.distanceKm, to.address.cityName)}</p>
            )}
          </div>
        </div>
        {/* Two or three words: the server's first sentence says it in full. */}
        {preview.alreadyThere ? (
          <p role="note" title={MOVE_WORDS.alreadyThereHover} className="rounded-lg bg-emerald-50 px-3 py-2 text-[13px] font-semibold text-emerald-900">{MOVE_WORDS.alreadyThere}</p>
        ) : preview.maybeAlreadyThere ? (
          <p role="note" title={MOVE_WORDS.maybeAlreadyThereHover} className="rounded-lg bg-amber-50 px-3 py-2 text-[13px] font-semibold text-amber-900">{MOVE_WORDS.maybeAlreadyThere}</p>
        ) : null}
        {/* The server's sentences, word for word. */}
        <ul aria-label={MOVE_WORDS.sentencesLabel} className="list-disc space-y-1 rounded-lg bg-white py-2 pl-7 pr-3 text-sm text-gray-800">
          {preview.sentences.map((line) => (
            <li key={line}>{localiseRaiseMessage(line)}</li>
          ))}
        </ul>
        <div className="flex flex-wrap items-center gap-2">
          {/* Focus lands on the safe answer: the button that was pressed has just left the page. */}
          <button type="button" autoFocus onClick={() => owner.current.onDone()} className={QUIET_SMALL}>
            {preview.confirm?.no || MOVE_WORDS.no}
          </button>
          <button
            type="button"
            onClick={() => {
              requestKey.current = null;
              reviewed.current = null;
              setPreview(null);
              setNotice(null);
              setStep('pick');
            }}
            className="px-1 text-xs font-medium text-primary-700 hover:underline"
          >
            {MOVE_WORDS.changeStore}
          </button>
          <button type="button" onClick={() => void confirm()} className={PRIMARY_SMALL}>
            {preview.confirm?.yes || MOVE_WORDS.yes}
          </button>
        </div>
        <p className="text-xs text-gray-500">{preview.confirm?.footnote || MOVE_WORDS.footnote}</p>
      </div>
    );
  }

  // Step 1: the new store, and what a refusal asked for.
  const reading = step === 'reading';
  const ready = !!store && !reading && (!needs.cityPicker || city != null);
  return (
    <div ref={box} role="group" aria-label={groupLabel} className={`${IN_VIEW} space-y-3 rounded-lg border border-gray-200 bg-white p-3`}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <p className="text-sm font-semibold text-gray-900">{MOVE_WORDS.heading}</p>
        <p className="min-w-0 truncate text-xs text-gray-500" title={fromWords}>{MOVE_WORDS.nowAt(fromWords)}</p>
      </div>
      {notice && <p role="status" className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">{notice}</p>}
      <StorePicker
        projectId={projectId}
        mode="move"
        label={MOVE_WORDS.store}
        value={store}
        onChange={pickStore}
        current={from.storeId}
        disabled={reading}
        onRefused={(err) => handledByOwner(err)}
      />
      {/* A refusal that asks for a city says so ("…for the new store below."): it is said above the city box. */}
      {refusal && needs.cityPicker && <FormErrorBanner message={refusal} />}
      {needs.cityPicker && (
        <div className="space-y-1">
          <CitySelect
            projectId={projectId}
            asking={false}
            label={MOVE_WORDS.city}
            value={city}
            onChange={setCity}
            disabled={reading}
            search={async (q) => (await hiringMoveAPI.cities(projectId, link.id, q))?.cities ?? []}
            onRefused={(err) => handledByOwner(err)}
          />
          <p className="text-xs text-gray-500">{MOVE_WORDS.cityHelp}</p>
        </div>
      )}
      {needs.staffingStatus && (
        <label className="flex items-start gap-2 text-sm text-gray-800">
          <input type="checkbox" className="mt-0.5" checked={confirmStatus} disabled={reading} onChange={(e) => setConfirmStatus(e.target.checked)} />
          <span>{MOVE_WORDS.tickStatus(needs.staffingStatus)}</span>
        </label>
      )}
      {needs.additional && store && (
        <label className="flex items-start gap-2 text-sm text-gray-800">
          <input type="checkbox" className="mt-0.5" checked={confirmAdditional} disabled={reading} onChange={(e) => setConfirmAdditional(e.target.checked)} />
          <span>{MOVE_WORDS.tickAdditional(moveStoreLabel(store))}</span>
        </label>
      )}
      {refusal && !needs.cityPicker && <FormErrorBanner message={refusal} />}
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => owner.current.onDone()} disabled={reading} className={QUIET_SMALL}>
          {MOVE_WORDS.close}
        </button>
        <button type="button" onClick={askReview} disabled={!ready} className={PRIMARY_SMALL}>
          {reading && <Loader2 className="h-3 w-3 animate-spin" />}
          {MOVE_WORDS.review}
        </button>
        {reading && <p role="status" className="text-xs text-gray-600">{MOVE_WORDS.reading}</p>}
      </div>
    </div>
  );
}

/**
 * A move that Recruitment has not confirmed: where it stands, and the one thing that can be
 * done about it. NOTHING HERE SENDS ANYTHING TO RECRUITMENT: "Check again" reads where the
 * opening is (at the new store's address, it was moved; still at the old one after the
 * settle time, it was not). There is no "Move again" here, on purpose.
 */
export function MoveStatus({
  projectId, link, canManage, message, onResult, onNothingOpen, onHiringGone,
}: {
  projectId: number;
  link: HiringLink;
  canManage: boolean;
  /** The last answer's sentence. */
  message: string | null;
  onResult: (result: HiringMoveResult) => void;
  /** The server has no move on record for this opening: nothing is open. `sentence` says so. */
  onNothingOpen: (sentence: string) => void;
  onHiringGone: () => void;
}) {
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const working = useRef(false);
  const alive = useRef(true);
  const latest = useRef({ onResult, onNothingOpen, onHiringGone });
  latest.current = { onResult, onNothingOpen, onHiringGone };

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const check = async () => {
    if (working.current) return;
    working.current = true;
    setChecking(true);
    setError(null);
    try {
      const result = await hiringMoveAPI.check(projectId, link.id);
      if (result && typeof result.outcome === 'string') latest.current.onResult(result);
      else if (alive.current) setError(MOVE_WORDS.checkFailed);
    } catch (err) {
      if (isHiringOff(err)) latest.current.onHiringGone();
      else if (hiringCodeOf(err) === MOVE_CODES.linkNotMoving) latest.current.onNothingOpen(moveErrorText(err, MOVE_WORDS.checkFailed));
      else if (alive.current) setError(moveErrorText(err, MOVE_WORDS.checkFailed));
    } finally {
      working.current = false;
      if (alive.current) setChecking(false);
    }
  };

  return (
    <div role="group" aria-label={MOVE_WORDS.standingLabel} className="space-y-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-3">
      <p className="text-sm font-semibold text-amber-900">{MOVE_WORDS.notConfirmed}</p>
      <p className="text-sm text-gray-900">{localiseRaiseMessage(message) || MOVE_WORDS.standing}</p>
      {error && <FormErrorBanner message={error} />}
      {canManage && (
        <button
          type="button"
          onClick={() => void check()}
          disabled={checking}
          className={`${QUIET_SMALL} inline-flex items-center gap-1`}
        >
          {checking && <Loader2 className="h-3 w-3 animate-spin" />}
          {checking ? MOVE_WORDS.checking : MOVE_WORDS.checkAgain}
        </button>
      )}
    </div>
  );
}

export default MoveStoreStep;
